import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import api from '../api/api';
import { trackABTestEvent } from '../utils/abTesting';
import '../styles/tsumugi-design.css';
import '../styles/pages/FridgePhotoPage.css';

/**
 * FridgePhotoPage - 冷蔵庫写真撮影→AI食材認識→レシピ生成
 * つむぎデザインシステム準拠
 *
 * フロー:
 * Step 1: 写真撮影/アップロード
 * Step 2: AI食材認識（Vision API）
 * Step 3: 認識結果確認・編集
 * → レシピ生成へ遷移
 */
const FridgePhotoPage = () => {
  const { user, plan } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // ページタイトル設定
  useEffect(() => {
    document.title = '冷蔵庫写真 | つむぎ';
  }, []);

  // ステップ管理
  const [currentStep, setCurrentStep] = useState(1);

  // 写真データ
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // AI認識データ
  const [recognizedIngredients, setRecognizedIngredients] = useState([]);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [recognitionError, setRecognitionError] = useState(null);

  // 手動追加用
  const [manualIngredient, setManualIngredient] = useState('');

  // 編集モード用
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');

  // 肉類選択アシスト用
  const [showMeatSelector, setShowMeatSelector] = useState(false);
  const [meatSelectorIndex, setMeatSelectorIndex] = useState(null);

  // 肉類の判定と選択肢
  const MEAT_VARIANTS = ['鶏肉', '鶏むね肉', '鶏もも肉', '鶏ささみ', '豚肉', '豚こま切れ', '豚バラ', '豚ロース', '牛肉', '牛こま切れ', '牛バラ', 'ひき肉', '合い挽き肉', 'ベーコン', 'ウインナー', 'ハム'];

  const isMeatIngredient = (ingredient) => {
    const meatKeywords = ['肉', '鶏', '豚', '牛', 'ひき', 'ベーコン', 'ウインナー', 'ハム'];
    return meatKeywords.some(keyword => ingredient.includes(keyword));
  };

  /**
   * 写真選択（カメラ/ファイル）
   */
  const handlePhotoSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // ファイルサイズチェック（5MB以下）
    if (file.size > 5 * 1024 * 1024) {
      alert('ファイルサイズは5MB以下にしてください');
      return;
    }

    // 画像タイプチェック
    if (!file.type.startsWith('image/')) {
      alert('画像ファイルを選択してください');
      return;
    }

    setPhoto(file);

    // プレビュー生成
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target.result);
      setCurrentStep(2); // Step 2へ進む
    };
    reader.readAsDataURL(file);

    // Track photo capture
    if (user?._id) {
      trackABTestEvent('RECIPE_NAMING', user._id, 'photo_capture_start', {
        source: 'fridge_photo_page',
        userPlan: plan
      });
    }
  };

  /**
   * AI食材認識実行
   */
  const handleRecognizeIngredients = async () => {
    if (!photo) return;

    setIsRecognizing(true);
    setRecognitionError(null);

    try {
      // FormDataで画像送信
      const formData = new FormData();
      formData.append('image', photo);

      const response = await api.post('/api/fridge/recognize', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setRecognizedIngredients(response.data.ingredients || []);
        setCurrentStep(3); // Step 3へ進む

        // Track successful recognition
        if (user?._id) {
          trackABTestEvent('RECIPE_NAMING', user._id, 'photo_capture_complete', {
            success: true,
            ingredientCount: response.data.ingredients?.length || 0,
            userPlan: plan
          });
        }
      } else {
        throw new Error(response.data.message || '認識に失敗しました');
      }
    } catch (error) {
      console.error('AI recognition error:', error);
      setRecognitionError(
        error.response?.status === 429
          ? '今月は上限までご利用いただきました。来月またご利用いただけます。'
          : error.response?.data?.message || 'AI認識に失敗しました。もう一度お試しください。'
      );

      // Track failed recognition
      if (user?._id) {
        trackABTestEvent('RECIPE_NAMING', user._id, 'photo_capture_complete', {
          success: false,
          error: error.response?.status === 429 ? 'quota_exceeded' : 'recognition_failed',
          userPlan: plan
        });
      }
    } finally {
      setIsRecognizing(false);
    }
  };

  /**
   * 食材の追加（手動）
   */
  const handleAddIngredient = () => {
    const trimmed = manualIngredient.trim();
    if (!trimmed) return;

    if (!recognizedIngredients.includes(trimmed)) {
      setRecognizedIngredients([...recognizedIngredients, trimmed]);
    }
    setManualIngredient('');
  };

  /**
   * 食材の削除
   */
  const handleRemoveIngredient = (ingredient) => {
    setRecognizedIngredients(recognizedIngredients.filter(item => item !== ingredient));
  };

  /**
   * 食材の編集開始
   */
  const handleStartEdit = (index, ingredient) => {
    // 肉類の場合は選択アシストを表示
    if (isMeatIngredient(ingredient)) {
      setMeatSelectorIndex(index);
      setShowMeatSelector(true);
    } else {
      // それ以外は直接編集モード
      setEditingIndex(index);
      setEditValue(ingredient);
    }
  };

  /**
   * 食材の編集確定
   */
  const handleConfirmEdit = () => {
    if (editingIndex !== null && editValue.trim()) {
      const newIngredients = [...recognizedIngredients];
      newIngredients[editingIndex] = editValue.trim();
      setRecognizedIngredients(newIngredients);
    }
    setEditingIndex(null);
    setEditValue('');
  };

  /**
   * 肉類選択アシストで選択
   */
  const handleSelectMeat = (meatType) => {
    if (meatSelectorIndex !== null) {
      const newIngredients = [...recognizedIngredients];
      newIngredients[meatSelectorIndex] = meatType;
      setRecognizedIngredients(newIngredients);
    }
    setShowMeatSelector(false);
    setMeatSelectorIndex(null);
  };

  /**
   * 肉類選択アシストをキャンセル
   */
  const handleCancelMeatSelector = () => {
    setShowMeatSelector(false);
    setMeatSelectorIndex(null);
  };

  /**
   * レシピ生成へ進む
   */
  const handleGenerateRecipe = () => {
    // 認識した食材をstateまたはlocalStorageに保存して遷移
    localStorage.setItem('fridgeIngredients', JSON.stringify(recognizedIngredients));
    navigate('/fridge-recipe-result');
  };

  /**
   * 最初からやり直す
   */
  const handleReset = () => {
    setCurrentStep(1);
    setPhoto(null);
    setPhotoPreview(null);
    setRecognizedIngredients([]);
    setRecognitionError(null);
    setManualIngredient('');
  };

  return (
    <AppLayout>
      <PageTitle
        title="冷蔵庫を撮影してレシピ生成"
        subtitle="写真を撮るだけ、食材を自動認識"
        icon="📷"
        backPath="/recipe-hub"
        backLabel="レシピ一覧"
      />

      <div className="tsumugi-container fridge-photo-page">
        {/* ステップインジケーター */}
        <div className="fridge-photo-page__steps mb-6">
          <div className="fridge-photo-page__steps-container">
            {/* Step 1 */}
            <div className={`fridge-photo-page__step ${currentStep >= 1 ? 'fridge-photo-page__step--active' : ''}`}>
              <div className="fridge-photo-page__step-circle">1</div>
              <div className="fridge-photo-page__step-label">写真撮影</div>
            </div>

            {/* Progress bar 1->2 */}
            <div className={`fridge-photo-page__step-line ${currentStep >= 2 ? 'fridge-photo-page__step-line--active' : ''}`}></div>

            {/* Step 2 */}
            <div className={`fridge-photo-page__step ${currentStep >= 2 ? 'fridge-photo-page__step--active' : ''}`}>
              <div className="fridge-photo-page__step-circle">2</div>
              <div className="fridge-photo-page__step-label">AI認識</div>
            </div>

            {/* Progress bar 2->3 */}
            <div className={`fridge-photo-page__step-line ${currentStep >= 3 ? 'fridge-photo-page__step-line--active' : ''}`}></div>

            {/* Step 3 */}
            <div className={`fridge-photo-page__step ${currentStep >= 3 ? 'fridge-photo-page__step--active' : ''}`}>
              <div className="fridge-photo-page__step-circle">3</div>
              <div className="fridge-photo-page__step-label">確認・編集</div>
            </div>
          </div>
        </div>

        {/* Step 1: 写真撮影/アップロード */}
        {currentStep === 1 && (
          <div className="fridge-photo-page__upload">
            <div className="tsumugi-card tsumugi-card--mint">
              <h3 className="tsumugi-card-title mb-4">📷 冷蔵庫の写真を撮影してください</h3>
              <p className="tsumugi-caption mb-4">
                冷蔵庫の中身を撮影すると、AIが食材を自動で認識します
              </p>

              {/* カメラ/ファイル選択ボタン */}
              <div className="fridge-photo-page__upload-buttons">
                {/* カメラ撮影（モバイル用） */}
                <button
                  className="fridge-photo-page__upload-btn tsumugi-btn tsumugi-btn-primary w-full mb-3"
                  onClick={() => cameraInputRef.current?.click()}
                >
                  📸 カメラで撮影
                </button>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />

                {/* ファイル選択 */}
                <button
                  className="fridge-photo-page__upload-btn tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--mint w-full"
                  onClick={() => fileInputRef.current?.click()}
                >
                  🖼️ ギャラリーから選択
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
              </div>

              {/* ヒント */}
              <div className="fridge-photo-page__hint mt-4 p-3 bg-white rounded-lg">
                <p className="text-sm text-gray-600">
                  💡 <strong>撮影のコツ:</strong>
                </p>
                <ul className="text-xs text-gray-600 mt-2 ml-4 space-y-1">
                  <li>• 明るい場所で撮影してください</li>
                  <li>• 食材が見やすいように整理すると精度UP</li>
                  <li>• 冷蔵庫全体が入るように撮影</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: AI認識 */}
        {currentStep === 2 && (
          <div className="fridge-photo-page__recognize">
            {/* 写真プレビュー */}
            <div className="tsumugi-card mb-4">
              <h3 className="tsumugi-card-title mb-3">📸 撮影した写真</h3>
              <div className="fridge-photo-page__preview">
                <img
                  src={photoPreview}
                  alt="冷蔵庫の写真"
                  className="fridge-photo-page__preview-image"
                />
              </div>
              <button
                className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--gray w-full mt-3"
                onClick={handleReset}
              >
                撮り直す
              </button>
            </div>

            {/* AI認識ボタン */}
            <div className="tsumugi-card tsumugi-card--mint">
              <h3 className="tsumugi-card-title mb-3">🤖 AI食材認識</h3>
              <p className="tsumugi-caption mb-4">
                写真から食材を自動で読み取ります（約10秒）
              </p>

              {recognitionError && (
                <div className="fridge-photo-page__error mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">❌ {recognitionError}</p>
                </div>
              )}

              <button
                className="tsumugi-btn tsumugi-btn-primary w-full"
                onClick={handleRecognizeIngredients}
                disabled={isRecognizing}
              >
                {isRecognizing ? (
                  <span className="flex items-center justify-center">
                    <span className="fridge-photo-page__spinner mr-2"></span>
                    認識中...
                  </span>
                ) : (
                  '🔍 食材を認識する'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: 認識結果確認・編集 */}
        {currentStep === 3 && (
          <div className="fridge-photo-page__result">
            <div className="tsumugi-card tsumugi-card--mint mb-4">
              <h3 className="tsumugi-card-title mb-3">✅ 認識された食材</h3>
              <p className="tsumugi-caption mb-4">
                認識結果を確認して、追加・削除できます
              </p>

              {/* 認識済み食材リスト */}
              <div className="fridge-photo-page__ingredients mb-4">
                {recognizedIngredients.length > 0 ? (
                  <div className="fridge-photo-page__ingredients-grid">
                    {recognizedIngredients.map((ingredient, index) => (
                      <div
                        key={index}
                        className={`fridge-photo-page__ingredient-tag ${isMeatIngredient(ingredient) ? 'fridge-photo-page__ingredient-tag--meat' : ''}`}
                      >
                        {editingIndex === index ? (
                          // 編集モード
                          <input
                            type="text"
                            className="fridge-photo-page__ingredient-edit-input"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleConfirmEdit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleConfirmEdit();
                              }
                              if (e.key === 'Escape') {
                                setEditingIndex(null);
                                setEditValue('');
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          // 通常表示（タップで編集）
                          <>
                            <span
                              className="fridge-photo-page__ingredient-name"
                              onClick={() => handleStartEdit(index, ingredient)}
                              title="タップで修正"
                            >
                              {ingredient}
                              {isMeatIngredient(ingredient) && <span className="fridge-photo-page__meat-hint">🥩</span>}
                            </span>
                            <button
                              className="fridge-photo-page__ingredient-remove"
                              onClick={() => handleRemoveIngredient(ingredient)}
                              aria-label="削除"
                            >
                              ×
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 text-center py-4">
                    食材が認識されませんでした。手動で追加してください。
                  </p>
                )}
              </div>

              {/* 肉類選択アシストモーダル */}
              {showMeatSelector && (
                <div className="fridge-photo-page__meat-selector-overlay" onClick={handleCancelMeatSelector}>
                  <div className="fridge-photo-page__meat-selector" onClick={(e) => e.stopPropagation()}>
                    <h4 className="fridge-photo-page__meat-selector-title">🥩 どのお肉ですか？</h4>
                    <p className="fridge-photo-page__meat-selector-hint">
                      タップして選んでください
                    </p>
                    <div className="fridge-photo-page__meat-selector-grid">
                      {MEAT_VARIANTS.map((meat) => (
                        <button
                          key={meat}
                          className="fridge-photo-page__meat-option"
                          onClick={() => handleSelectMeat(meat)}
                        >
                          {meat}
                        </button>
                      ))}
                    </div>
                    <button
                      className="fridge-photo-page__meat-selector-cancel"
                      onClick={handleCancelMeatSelector}
                    >
                      キャンセル
                    </button>
                  </div>
                </div>
              )}

              {/* 手動追加 */}
              <div className="fridge-photo-page__manual-add">
                <p className="text-sm font-semibold text-gray-700 mb-2">📝 食材を追加</p>
                <input
                  type="text"
                  className="tsumugi-form-input w-full"
                  placeholder="例: 鶏もも肉（Enterまたはカンマで追加）"
                  value={manualIngredient}
                  onChange={(e) => setManualIngredient(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddIngredient();
                    }
                  }}
                  onBlur={() => {
                    // 入力欄から離れたら自動追加
                    if (manualIngredient.trim()) {
                      handleAddIngredient();
                    }
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">💡 入力後にEnterキーまたは他の場所をタップすると追加されます</p>
              </div>
            </div>

            {/* レシピ生成ボタン */}
            <div className="fridge-photo-page__actions">
              <button
                className="tsumugi-btn tsumugi-btn-primary w-full mb-3"
                onClick={handleGenerateRecipe}
                disabled={recognizedIngredients.length === 0}
              >
                🍳 レシピを作る（{recognizedIngredients.length}個の食材）
              </button>
              <button
                className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--gray w-full"
                onClick={handleReset}
              >
                最初からやり直す
              </button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default FridgePhotoPage;
